import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { Sheet, SheetContent } from '@/components/ui/sheet.tsx';
import { AppShellContext, type AppShellContextValue } from './AppShellContext.ts';

/**
 * AppShell — pure layout primitive (spec §9.1).
 *
 * Composition slots:
 *   • topbar    — fixed-height header. Stretches full width on mobile;
 *                 sits to the right of the persistent drawer on desktop.
 *   • drawer    — sidebar nav. Persistent <aside> at md+. Slides in as a
 *                 Sheet at sm-. ESC / backdrop / swipe to close, scroll
 *                 lock, focus trap — all handled by Radix.
 *   • bottomNav — fixed-bottom destination strip. Mobile-only; the
 *                 component itself should hide at md+ via its own classes.
 *   • banners   — sticky banner stack rendered between topbar and main.
 *                 Pass <BannerList /> here.
 *   • children  — the route content.
 *
 * AppShell is auth-agnostic — it does not know who the user is. App.tsx
 * decides what to put in each slot (and whether to render AppShell at
 * all). That keeps the layout primitive reusable on admin sub-shells,
 * NOC wall mode, etc.
 *
 * The shell collapses the drawer automatically on route change so
 * mobile users don't have to tap close after tapping a nav item.
 *
 * Safe-area padding (iOS notch / home indicator) is applied to the
 * topbar (top), bottomNav (bottom), and main (additional bottom space
 * when bottomNav is present so content isn't hidden behind it).
 */
export interface AppShellProps {
  topbar?: ReactNode;
  drawer?: ReactNode;
  bottomNav?: ReactNode;
  banners?: ReactNode;
  children: ReactNode;
}

export function AppShell({ topbar, drawer, bottomNav, banners, children }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Auto-close drawer on route change. Without this, mobile users tap a
  // nav item, navigate, then have to tap × manually.
  const [drawerPath, setDrawerPath] = useState(location.pathname);
  if (location.pathname !== drawerPath) {
    setDrawerPath(location.pathname);
    setDrawerOpen(false);
  }

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const ctx = useMemo<AppShellContextValue>(
    () => ({ drawerOpen, openDrawer, closeDrawer }),
    [drawerOpen, openDrawer, closeDrawer],
  );

  return (
    <AppShellContext.Provider value={ctx}>
      <div className="flex min-h-svh bg-background text-foreground">
        {/* Persistent drawer at md+. */}
        {drawer ? (
          <aside
            aria-label="Primary navigation"
            className="hidden md:flex md:flex-col md:w-56 md:flex-shrink-0 md:border-r md:border-border md:bg-bg-subtle"
          >
            {drawer}
          </aside>
        ) : null}

        {/* Mobile drawer — Sheet from the left. Radix handles scroll
            lock, focus trap, ESC, and backdrop. The Sheet's built-in
            close button (top-right corner of SheetContent) is the only
            close affordance — SideNav must not add its own to avoid
            dual-close-button UX. */}
        {drawer ? (
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetContent
              side="left"
              className="md:hidden w-72 max-w-[85vw] p-0 bg-bg-subtle"
            >
              {drawer}
            </SheetContent>
          </Sheet>
        ) : null}

        {/* Main column: topbar + banners + scrollable content. */}
        <div className="flex flex-col flex-1 min-w-0">
          {topbar ? (
            <header className="flex-shrink-0 sticky top-0 z-30 border-b border-border bg-card pt-[env(safe-area-inset-top)]">
              {topbar}
            </header>
          ) : null}

          {banners ? <div className="flex-shrink-0">{banners}</div> : null}

          <main
            className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)] data-[has-bottom-nav=true]:pb-[calc(env(safe-area-inset-bottom)+4rem)] md:data-[has-bottom-nav=true]:pb-[env(safe-area-inset-bottom)]"
            data-has-bottom-nav={bottomNav ? 'true' : 'false'}
          >
            {children}
          </main>
        </div>

        {/* Mobile bottom-nav — the component handles its own `md:hidden`
            and fixed positioning. */}
        {bottomNav}
      </div>
    </AppShellContext.Provider>
  );
}
